import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { Attendance, Student } from '@/lib/types';
import { DAY_NAMES, formatTime } from '@/lib/constants';
import { useProfile } from '@/hooks/useProfile';
import MakeupScheduleModal from './components/MakeupScheduleModal';

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shortDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-').map(Number);
  return `${m}/${d}`;
}

function displayDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${y}.${m}.${d} (${DAY_NAMES[date.getDay()]})`;
}

export default function AbsenceCalendarPage() {
  // viewDate 는 항상 해당 월의 1일로 유지
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [records, setRecords] = useState<Attendance[]>([]);
  const [unscheduled, setUnscheduled] = useState<Attendance[]>([]);
  const [studentMap, setStudentMap] = useState<Record<string, Student>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<Attendance | null>(null);

  const { profile, loading: profileLoading } = useProfile();

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const loadData = useCallback(async () => {
    const academyId = profile?.academy_id;
    if (!academyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const mm = String(month + 1).padStart(2, '0');
      const monthStart = `${year}-${mm}-01`;
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const monthEnd = `${year}-${mm}-${String(daysInMonth).padStart(2, '0')}`;

      const [attRes, unscheduledRes, studentsRes] = await Promise.all([
        supabase
          .from('attendance')
          .select('*')
          .in('status', ['absent', 'makeup'])
          .gte('date', monthStart)
          .lte('date', monthEnd),
        supabase
          .from('attendance')
          .select('*')
          .eq('status', 'absent')
          .is('makeup_date', null)
          .or('makeup_status.is.null,makeup_status.eq.pending')
          .order('date', { ascending: false }),
        supabase.rpc('get_students_by_academy', { p_academy_id: academyId }),
      ]);

      if (attRes.error) throw attRes.error;
      if (unscheduledRes.error) throw unscheduledRes.error;
      if (studentsRes.error) throw studentsRes.error;

      const sm: Record<string, Student> = {};
      ((studentsRes.data as Student[]) || []).forEach((s) => {
        sm[s.id] = s;
      });

      setRecords((attRes.data as Attendance[]) || []);
      setUnscheduled((unscheduledRes.data as Attendance[]) || []);
      setStudentMap(sm);
    } catch (err) {
      setError('결석 정보를 불러오지 못했어요.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [year, month, profile]);

  useEffect(() => {
    if (!profileLoading && profile?.academy_id) loadData();
  }, [loadData, profileLoading, profile]);

  function changeMonth(amount: number) {
    setViewDate(new Date(year, month + amount, 1));
  }

  async function scheduleMakeup(date: string, time: string) {
    if (!scheduleTarget) return;
    setSaving(true);
    try {
      const wasCompleted = scheduleTarget.makeup_status === 'completed';
      const { error: err } = await supabase
        .from('attendance')
        .update({
          makeup_date: date,
          makeup_time: time || null,
          makeup_status: wasCompleted ? 'completed' : 'pending',
          // 이미 완료된 보충이면 완료일도 함께 갱신해서 상태가 어긋나지 않게 해요
          makeup_completed_date: wasCompleted ? date : scheduleTarget.makeup_completed_date,
        })
        .eq('id', scheduleTarget.id);
      if (err) throw err;
      setScheduleTarget(null);
      await loadData();
    } catch (err) {
      setError('보충 일정 저장에 실패했어요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  const byDate = useMemo(() => {
    const map: Record<string, Attendance[]> = {};
    records.forEach((r) => {
      if (!map[r.date]) map[r.date] = [];
      map[r.date].push(r);
    });
    return map;
  }, [records]);

  const cells = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) result.push(null);
    for (let d = 1; d <= daysInMonth; d++) result.push(d);
    return result;
  }, [year, month]);

  const todayStr = toDateStr(new Date());

  const scheduledCount = records.filter(
    (r) => r.makeup_status !== 'completed' && !!r.makeup_date
  ).length;
  const completedCount = records.filter((r) => r.makeup_status === 'completed').length;
  const unscheduledInMonth = records.filter(
    (r) => r.makeup_status !== 'completed' && !r.makeup_date
  ).length;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground-950">결석보충달력</h2>
          <p className="mt-1 text-sm text-foreground-500">
            결석한 학생만 월별 달력에 표시돼요. 보충을 잡으면 일정이, 마치면 완료가 함께 나와요.
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-primary-600">
            <i className="ri-cursor-line"></i>
            달력 칸의 학생 이름을 클릭하면 바로 보충 일정을 잡거나 수정할 수 있어요.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => changeMonth(-1)}
            title="이전 달"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="min-w-[120px] text-center text-base font-bold text-foreground-950">
            {year}년 {month + 1}월
          </span>
          <button
            onClick={() => changeMonth(1)}
            title="다음 달"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>

      {/* 요약 */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-lg border border-background-200 bg-white p-4">
          <p className="text-sm text-foreground-500">이번 달 결석</p>
          <p className="mt-1 text-2xl font-bold text-foreground-950">{records.length}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-white p-4">
          <p className="text-sm text-foreground-500">보충 예정</p>
          <p className="mt-1 text-2xl font-bold text-secondary-600">{scheduledCount}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-white p-4">
          <p className="text-sm text-foreground-500">보충 완료</p>
          <p className="mt-1 text-2xl font-bold text-primary-600">{completedCount}</p>
        </div>
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-4">
          <p className="text-sm text-accent-700">보충 미정 (이번 달)</p>
          <p className="mt-1 text-2xl font-bold text-accent-700">{unscheduledInMonth}</p>
        </div>
      </div>

      {/* 범례 */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-600">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-accent-500"></span> 보충 미정 (아직 안 잡음)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-secondary-400"></span> 보충 예정
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-primary-500"></span> 보충 완료
        </span>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
          <span>{error}</span>
          <button
            onClick={loadData}
            className="cursor-pointer whitespace-nowrap font-semibold underline"
          >
            다시 시도
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500"></i>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
          {/* 요일 헤더 */}
          <div className="grid grid-cols-7 border-b border-background-200 bg-background-50">
            {DAY_NAMES.map((d) => (
              <div
                key={d}
                className="px-2 py-2 text-center text-xs font-semibold text-foreground-500"
              >
                {d}
              </div>
            ))}
          </div>

          {/* 달력 그리드 */}
          <div className="grid grid-cols-7">
            {cells.map((day, idx) => {
              if (day === null) {
                return (
                  <div
                    key={`blank-${idx}`}
                    className="min-h-[96px] border-r border-b border-background-100 last:border-r-0"
                  ></div>
                );
              }
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayRecords = byDate[dateStr] || [];
              const isToday = dateStr === todayStr;
              return (
                <div
                  key={day}
                  className="min-h-[96px] border-r border-b border-background-100 p-1.5 last:border-r-0"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                        isToday ? 'bg-primary-500 text-background-50' : 'text-foreground-700'
                      }`}
                    >
                      {day}
                    </span>
                  </div>

                  <div className="mt-1 space-y-1">
                    {dayRecords.map((r) => {
                      const completed = r.makeup_status === 'completed';
                      const scheduled = !completed && !!r.makeup_date;
                      const student = studentMap[r.student_id];
                      const doneDate = completed ? r.makeup_completed_date || r.makeup_date : null;
                      const statusText = completed
                        ? `보충완료 · ${shortDate(doneDate)}`
                        : scheduled
                        ? `보충예정 · ${shortDate(r.makeup_date)}`
                        : '보충 미정';
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setScheduleTarget(r)}
                          title={`${student?.name || '학생'} · ${r.absence_type || '결석'}${
                            r.absence_type === '기타' && r.note ? `(${r.note})` : ''
                          } · ${statusText} — 클릭해서 보충 일정 ${
                            completed || scheduled ? '수정' : '잡기'
                          }`}
                          className={`block w-full cursor-pointer rounded px-1.5 py-1 text-left text-[11px] leading-tight transition-all hover:ring-2 hover:ring-primary-300 ${
                            completed
                              ? 'bg-primary-100 text-primary-800'
                              : scheduled
                              ? 'bg-secondary-100 text-secondary-800'
                              : 'bg-accent-500 text-background-50'
                          }`}
                        >
                          <span className="block truncate font-medium">
                            {student?.name || '삭제된 학생'}
                          </span>
                          <span className="block truncate text-[10px] opacity-90">
                            {statusText}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 보충 잡지 않은 결석 */}
      <div className="overflow-hidden rounded-lg border border-accent-200 bg-white">
        <div className="flex flex-col gap-1 border-b border-accent-200 bg-accent-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-error-warning-line text-lg"></i>
            </span>
            <div>
              <span className="text-sm font-bold text-foreground-950">보충 잡지 않은 결석</span>
              <span className="ml-2 text-xs text-foreground-500">
                아직 보충 일정을 정하지 못한 결석이에요
              </span>
            </div>
          </div>
          <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-800">
            {unscheduled.length}건
          </span>
        </div>

        {unscheduled.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-check-double-line text-2xl"></i>
            </div>
            <p className="mt-3 text-sm font-medium text-foreground-700">
              보충 일정이 안 잡힌 결석이 없어요.
            </p>
            <p className="mt-1 text-xs text-foreground-400">
              모든 결석에 보충 일정이 잡혀 있거나 보충이 완료됐어요.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {unscheduled.map((r) => {
              const student = studentMap[r.student_id];
              return (
                <div
                  key={r.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-sm font-bold text-accent-700">
                      {student?.name.charAt(0) || '?'}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900">
                          {student?.name || '삭제된 학생'}
                        </span>
                        {student?.grade && (
                          <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">
                            {student.grade}
                          </span>
                        )}
                        <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-800">
                          보충 미정
                        </span>
                      </div>
                      <span className="text-xs text-foreground-500">
                        결석일 {displayDate(r.date)} · {r.absence_type || '결석'}
                        {r.absence_type === '기타' && r.note ? `(${r.note})` : ''}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setScheduleTarget(r)}
                    className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-calendar-schedule-line"></i> 보충 일정 잡기
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MakeupScheduleModal
        open={!!scheduleTarget}
        studentName={scheduleTarget ? studentMap[scheduleTarget.student_id]?.name || '학생' : ''}
        absenceDate={scheduleTarget ? displayDate(scheduleTarget.date) : ''}
        initialDate={scheduleTarget?.makeup_date || null}
        initialTime={scheduleTarget?.makeup_time || null}
        onClose={() => setScheduleTarget(null)}
        onSave={scheduleMakeup}
      />
    </div>
  );
}